import { Dropdown, InputGroupText, NumericTextField, TextField, VideoInputGroupText } from "app/components/CustomFormElements/syncFusionFormFields";
import { videoValidationSchema } from "app/helper/validationSchema/productValidationSchema";
import { useFormik } from "formik";
import * as Yup from "yup";
import { actionError, actionSuccess, hideLoader, showLoader } from "middleware/actions/utilityAction";
import { addProductVideo, editProductVideo } from "middleware/services/productApi";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import { useHistory } from "react-router-dom";
import { fetchVideoThumbUrl } from "middleware/services/productApi";
import { PRODUCT_LOGO_IMG_SIZE } from "config";
import { fetchSingleCompany } from "middleware/services/companyApi";
import { setBreadCrumb } from "middleware/actions/breadCrumbAction";
import PropTypes from "prop-types";
import { accessRightActionCheck } from "utilities";
import { COMMON_ACCESS_ACTION } from "../../../../../../../../constants";

const VideoModal = (props) => {
  const { companyId, productId, videoId } = props.computedMatch.params || {};
  const { accessRights: aR } = props;
  const videoThumbRef = useRef(null);
  const videoThumbInputRef = useRef(null);
  const [imageError, setImageError] = useState("");
  const [oldData, setOldData] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [productName, setProductName] = useState("");
  const [interfaceActionAccess, setInterfaceActionAccess] = useState([]);

  const { productDetails = {} } = useSelector((state) => state.productState);
  const split_url = props.location.pathname.split("/");
  const backPath = props?.editData ? `${split_url.slice(0, -3).join("/")}/edit#overview` : `${split_url.slice(0, -2).join("/")}/edit#overview`;
  const dispatch = useDispatch();
  const history = useHistory();

  const initialFormValues = {
    videoTitle: "",
    videoDescription: "",
    videoSource: "Vimeo",
    videoId: "",
    videoDurationInMinute: "",
    videoThumbnail: "",
  };

  /* Interface action access from common access */
  useEffect(() => {
    let interfaceName = "Overview";
    let actionAccess = accessRightActionCheck(aR.moduleName, interfaceName);
    setInterfaceActionAccess(actionAccess);
  }, []);

  /* Common access */
  const accessPermissionAdd = interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.ADD_VIDEOS);
  const accessPermissionEdit = interfaceActionAccess?.includes(COMMON_ACCESS_ACTION.EDIT_VIDEOS);

  const formik = useFormik({
    initialValues: oldData || initialFormValues,
    validationSchema: Yup.object().shape(videoValidationSchema),
    enableReinitialize: true,
  });

  const breadCrumbLinks = useMemo(() => {
    const links = [{ linkUrl: "/company", linkName: `Company`, linkActive: false }];
    if (companyId && companyName) {
      links.push({ linkUrl: `/company/${companyId}/edit#basic`, linkName: companyName, linkActive: false });
      links.push({ linkUrl: `/company/${companyId}/edit#basic`, linkName: aR.moduleName, linkActive: false });
    }

    if (productId && productName) {
      links.push({
        linkUrl: `/company/${companyId}/product/${productId}/edit#overview`,
        linkName: productName,
        linkActive: false,
      });
      links.push({
        linkUrl: `/company/${companyId}/product/${productId}/edit#overview`,
        linkName: "Video",
        linkActive: false,
      });
      links.push({ linkUrl: ``, linkName: props.editData ? "Edit" : "Add New", linkActive: true });
    }

    return links;
  }, [companyId, companyName, productId, productName]);

  const readVideoThumnail = (imageFile) => {
    let reader = new FileReader();
    reader.onload = (e) => {
      videoThumbRef.current.setAttribute("src", e.target.result);
    };
    reader.readAsDataURL(imageFile);
  };

  const handleReset = () => {
    formik.resetForm();
    if (props.editData) {
      formik.setValues(oldData);
    }
  };

  const onSaveData = async (data) => {
    const formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }
    try {
      dispatch(showLoader());
      await addProductVideo(productId, formData);
      dispatch(hideLoader());
      dispatch(actionSuccess("Video saved successfully."));
      handleReset();
      history.push(backPath, { shouldRefresh: true });
    } catch (error) {
      dispatch(actionError(error.data.message || "Something went wrong."));
    } finally {
      dispatch(hideLoader());
    }
  };

  const onUpdateData = async (data) => {
    const formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }
    try {
      dispatch(showLoader());
      await editProductVideo(productId, videoId, formData);
      dispatch(hideLoader());
      dispatch(actionSuccess("Video updated successfully."));
      handleReset();
      history.push(backPath, { shouldRefresh: true });
    } catch (error) {
      dispatch(actionError(error.data.message || "Something went wrong."));
    } finally {
      dispatch(hideLoader());
    }
  };

  const setFormikValues = (data) => {
    const oldData = {
      videoTitle: data.videoTitle ? data.videoTitle : "",
      videoDescription: data.videoDescription || "",
      videoSource: data.videoSource || "",
      videoId: data.videoId || "",
      videoDurationInMinute: data.videoDuration || "",
      videoThumbnail: data.videoThumbnail || "",
    };
    setOldData(oldData);
    setTimeout(() => {
      formik.setValues(oldData);
    }, 1);
  };

  const saveDocumentData = () => {
    const { values } = formik;
    const productDocumentData = {
      videoTitle: values.videoTitle,
      videoDescription: values.videoDescription,
      videoSource: values.videoSource,
      videoId: values.videoId,
      videoDuration: values.videoDurationInMinute,
      videoThumbnail: values.videoThumbnail,
    };
    props.editData ? onUpdateData(productDocumentData) : onSaveData(productDocumentData);
  };

  const handleSubmitVideo = () => {
    formik.validateForm().then((res) => {
      if (Object.keys(res).length) {
        const touched = {};
        Object.keys(res).forEach((field) => {
          touched[field] = true;
        });
        formik.setFormikState({ ...formik, touched: touched, errors: res });
      } else {
        saveDocumentData();
      }
    });
  };

  const setVideo = () => {
    const videoData = productDetails.productVideos.find((video) => video.id === videoId);
    if (videoData) {
      setFormikValues(videoData);
    }
  };

  useEffect(() => {
    if (props.editData && productDetails) {
      formik.resetForm();
      setVideo();
    } else {
      videoThumbInputRef.current.value = "";
      formik.resetForm();
    }
  }, [props.editData, productId, videoId]);

  const getCompanyName = async () => {
    if (companyId) {
      if (Object.keys(productDetails).length > 0) {
        setCompanyName(productDetails.productCompany.companyName);
      } else {
        const fields = { fields: ["id", "companyName"] };
        try {
          const response = await fetchSingleCompany(companyId, { ...fields });
          setCompanyName(response.data.data.companyName);
        } catch (error) {
          dispatch(actionError(error.data.message || "something went wrong."));
        }
      }
    }
  };

  useEffect(() => {
    setProductName(productDetails.productName);
    getCompanyName();
  }, [productDetails, dispatch]);

  useEffect(() => {
    dispatch(setBreadCrumb(breadCrumbLinks));

    return () => {
      dispatch(setBreadCrumb([]));
    };
  }, [dispatch, breadCrumbLinks]);

  useEffect(() => {
  ReactTooltip.rebuild();
}, [formik.values?.videoSource]);

  return (
    <div className="p-0 mx-3 product-video-modal">
      <div className="modal-header">Products &amp; Technology - Video</div>
      <div className="modal-body">
        <div className="video-content">
          <div className="form-row">
            <div className="video-title">
              {" "}
              <TextField formik={formik} placeholder="Title *" id="videoTitle" name="videoTitle" required="required" />
            </div>
            <div className="video-source">
              {" "}
              <Dropdown
                formik={formik}
                required="required"
                placeholder="Video Source"
                id="videoSource"
                name="videoSource"
                //  fields={{ text: "label", value: "value" }}
                dataSource={["Vimeo", "YouTube"]}
              />
            </div>
            <div className="video-input-group">
              <VideoInputGroupText
                formik={formik}
                inputGroupText={
                  formik.values.videoSource === "Vimeo"
                    ? "https://player.vimeo.com/video/"
                    : (formik.values.videoSource === "YouTube" && "https://www.youtube.com/") || ""
                }
                type="text"
                id="videoId"
                placeholder={`Video ID *`}
                name="videoId"
              />
              <div
                className="icon-tip"
                data-for="video-info-tooltip"
                data-tip={`e.g link : https://${
                  formik.values.videoSource === "Vimeo" ? "vimeo.com" : "youtube.com"
                }/428464282/899849fa06,<br/> id : 428464282/899849fa06`}
              >
                <img src="/assets/info-icon.svg" alt="info" />
                <ReactTooltip id="video-info-tooltip" effect="solid" multiline={true} delay={1000} />
              </div>
            </div>
            <div className="video-duration">
              <NumericTextField
                formik={formik}
                format="n0"
                min={0}
                id="videoDurationInMinute"
                placeholder="Duration in Minutes *"
                name="videoDurationInMinute"
              />
            </div>
          </div>
          <div className="px-3">
            <p style={{ fontSize: "14px", fontWeight: "700", color: "#4B4B55", lineHeight: 1 }}>Upload Thumbnail *</p>
            <div className="row m-0">
              <div className="product-logo-holder p-0">
                <div>
                  <img
                    src={formik.values?.videoThumbnail ? fetchVideoThumbUrl(formik.values.videoThumbnail) : "/images/default_company_image.jpg"}
                    alt="Product-image"
                    ref={videoThumbRef}
                    className="object-cover"
                  />
                </div>
                <div className="product-logo-action" style={{ top: "2px", right: "6px" }}>
                  <span onClick={() => videoThumbInputRef.current.click()}>
                    <i className="fa fa-pencil-alt" aria-hidden="true"></i>
                  </span>
                  {formik.values?.videoThumbnail && (
                  <span onClick={() => {
                   formik.setFieldValue("videoThumbnail", null);
                   if (videoThumbInputRef.current) {
                   videoThumbInputRef.current.value = "";
                     }
                    setImageError("");
                     videoThumbRef.current.src = "/images/default_company_image.jpg"; // Reset preview
                    }}>
                  <i className="fa fa-trash ml-1" aria-hidden="true"></i>
                    </span>
                  )}
                </div>
                <input
                  type="file"
                  className="d-none"
                  accept=".jpg, .png"
                  id="videoThumbnail"
                  name="videoThumbnail"
                  ref={videoThumbInputRef}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (e.target.files.length > 0) {
                      if ((file.type == "image/png" || file.type == "image/jpg" || file.type == "image/jpeg") && file.size < PRODUCT_LOGO_IMG_SIZE) {
                        setImageError("");
                        readVideoThumnail(file);
                        if (videoThumbInputRef.current.files.length)
                          formik.setValues({ ...formik.values, videoThumbnail: videoThumbInputRef.current.files[0] });
                      } else {
                        setImageError("Only JPG/JPEG or PNG files are allowed. Max file size allowed is 10MB.");
                      }
                    }
                  }}
                />
              </div>
              <div className="ml-3">
                <p className="mb-0" style={{ fontSize: "14px", fontWeight: "700", color: "#4B4B55", lineHeight: 1 }}>
                  Note:
                </p>
                <ul className="ml-4" style={{ fontSize: "14px", fontWeight: "400", color: "#000", lineHeight: 1.2 }}>
                  <li>Only JPG/JPEG or PNG files are allowed.</li>
                  <li>Max file size allowed is 10MB.</li>
                  <li>Thumbnail Dimensions: 350W x 200H</li>
                </ul>
              </div>
            </div>
            <div className={`image-error ${formik.touched.videoThumbnail === true ? "show" : "hide"}`} role="alert">
              <small>{formik.errors.videoThumbnail ? formik.errors.videoThumbnail : ""}</small>
            </div>
            <div className={`image-error ${imageError ? "show" : "hide"}`} role="alert">
              <small>{imageError ? imageError : ""}</small>
            </div>
          </div>
        </div>

        <div className="text-right border-top p-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleReset();
              setImageError("");
              history.push(backPath);
            }}
            className="btn btn-outline-dark mr-3 company-button"
          >
            Cancel
          </button>

          {accessPermissionAdd || accessPermissionEdit ? (
            <button type="button" className="btn btn-primary company-button" onClick={handleSubmitVideo} disabled={false}>
              {props?.editData ? "Update" : "Save"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VideoModal;

VideoModal.propTypes = {
  accessRights: PropTypes.object,
  location: PropTypes.object,
  computedMatch: PropTypes.object,
  editData: PropTypes.bool,
};
